import {useEffect, useMemo, useRef, useState} from '@wordpress/element';

const VIEWBOX = 586;
const CENTER = VIEWBOX / 2;

const OUTER_RADIUS = 196;
const INNER_RADIUS = 98;

const LABEL_RADIUS = (OUTER_RADIUS / 2) + (INNER_RADIUS / 2);
const LABEL_INNER_RADIUS = LABEL_RADIUS - 10;
const LABEL_OUTER_RADIUS = LABEL_RADIUS + 10;

const RING_RADIUS = 215;

const ITEMS_RADIUS = 265;
const ITEMS_PADDING = 12;

const INTERVAL_TIMEOUT = 1500;

function colorToRgbObject(color) {
	if (!color) {
		return {r: 255, g: 255, b: 255};
	}

	// rgb(...) oder rgba(...)
	const rgbMatch = color.match(
		/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+\s*)?\)/
	);

	if (rgbMatch) {
		return {
			r: Number(rgbMatch[1]),
			g: Number(rgbMatch[2]),
			b: Number(rgbMatch[3]),
		};
	}

	// hex
	const normalized = color.replace('#', '');
	const full = normalized.length === 3
		? normalized.split('').map((part) => part + part).join('')
		: normalized;

	const int = Number.parseInt(full, 16);

	if (Number.isNaN(int)) {
		return {r: 255, g: 255, b: 255};
	}

	return {
		r: (int >> 16) & 255,
		g: (int >> 8) & 255,
		b: int & 255,
	};
}

/**
 * Converts degree to radius, with an offset of -90° to start at the top of the circle.
 *
 * @param {number} deg
 * @returns {number}
 */
function degToRadius(deg) {
	return ((deg - 90) * Math.PI) / 180;
}

/**
 * Returns the path definitions for segment labels, which are used to position the text along the arc of the segment.
 *
 * @param {number} radius
 * @param {number} startDeg
 * @param {number} endDeg
 * @param {boolean} flip
 * @returns {string}
 */
function describeLabelPath(radius, startDeg, endDeg, flip = false) {
	const pathStartDeg = flip ? endDeg : startDeg;
	const pathEndDeg = flip ? startDeg : endDeg;

	const start = pointOnCircle(radius, pathStartDeg);
	const end = pointOnCircle(radius, pathEndDeg);

	const largeArcFlag = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
	const sweepFlag = flip ? 0 : 1;

	return [
		`M ${start.x} ${start.y}`,
		`A ${radius} ${radius} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`,
	].join(' ');
}

/**
 * Returns the path definition for the segment ring, which is a slightly larger arc around the segment to create a highlighted border effect.
 *
 * @param {number} startDeg
 * @param {number} endDeg
 * @returns {string}
 */
function describeSegmentRingPath(startDeg, endDeg) {
	const thickness = 6;
	const r = RING_RADIUS;
	const half = thickness / 2;

	const outerRadius = r + half;
	const innerRadius = r - half;

	const startOuter = pointOnCircle(outerRadius, startDeg);
	const endOuter = pointOnCircle(outerRadius, endDeg);
	const startInner = pointOnCircle(innerRadius, startDeg);
	const endInner = pointOnCircle(innerRadius, endDeg);

	const largeArcFlag = endDeg - startDeg > 180 ? 1 : 0;

	return [
		// Start
		`M ${startOuter.x} ${startOuter.y}`,
		// Outer
		`A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${endOuter.x} ${endOuter.y}`,
		// Rounding (End)
		`A ${half} ${half} 0 0 1 ${endInner.x} ${endInner.y}`,
		// Inner
		`A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${startInner.x} ${startInner.y}`,
		// Rounding (Start)
		`A ${half} ${half} 0 0 1 ${startOuter.x} ${startOuter.y}`,
		'Z',
	].join(' ');
}

/**
 * Returns the path definition for the segment.
 *
 * @param startDeg
 * @param endDeg
 * @returns {string}
 */
function describeSegmentPath(startDeg, endDeg) {
	const outerStart = pointOnCircle(OUTER_RADIUS, startDeg);
	const outerEnd = pointOnCircle(OUTER_RADIUS, endDeg);
	const innerEnd = pointOnCircle(INNER_RADIUS, endDeg);
	const innerStart = pointOnCircle(INNER_RADIUS, startDeg);
	const largeArcFlag = endDeg - startDeg > 180 ? 1 : 0;

	return [
		// Start
		`M ${outerStart.x} ${outerStart.y}`,
		// Outer
		`A ${OUTER_RADIUS} ${OUTER_RADIUS} 0 ${largeArcFlag} 1 ${outerEnd.x} ${outerEnd.y}`,
		// End
		`L ${innerEnd.x} ${innerEnd.y}`,
		// Inner
		`A ${INNER_RADIUS} ${INNER_RADIUS} 0 ${largeArcFlag} 0 ${innerStart.x} ${innerStart.y}`,
		'Z',
	].join(' ');
}

/**
 *
 *
 * @param {number} start
 * @param {number} end
 * @param {number} count
 * @param {number} padding
 * @returns {number[]|unknown[]}
 */
function distributeAngles(start, end, count, padding = ITEMS_PADDING) {
	const from = start + padding;
	const to = end - padding;

	if (count <= 1) {
		return [(from + to) / 2];
	}

	return Array.from({length: count}, (_, i) => {
		return from + ((to - from) * i) / (count - 1);
	});
}

/**
 * Calculate the shortest angle delta between two angles, which is used to determine the direction and amount of rotation needed to move from one angle to another.
 *
 * @param {number} from
 * @param {number} to
 * @returns {number}
 */
function getShortestAngleDelta(from, to) {
	let delta = normalizeAngle(to) - normalizeAngle(from);

	if (delta > 180) {
		delta -= 360;
	} else if (delta < -180) {
		delta += 360;
	}

	return delta;
}

/**
 * Get the shortest rotation to a desired location
 *
 * @param {number} currentRotation
 * @param {number} desiredRotation
 * @returns {number}
 */
function getShortestRotation(currentRotation, desiredRotation) {
	const currentNormalized = normalizeAngle(currentRotation);
	const desiredNormalized = normalizeAngle(desiredRotation);

	let delta = desiredNormalized - currentNormalized;

	if (delta > 180) {
		delta -= 360;
	} else if (delta < -180) {
		delta += 360;
	}

	return currentRotation + delta;
}

/**
 * Calculate rgba value from hex value.
 *
 * @param {string} hex
 * @param {number} alpha
 * @returns {string}
 */
function hexToRgba(hex, alpha = 1) {
	const normalized = hex.replace('#', '');
	const full = normalized.length === 3
		? normalized.split('').map((part) => part + part).join('')
		: normalized;

	const int = Number.parseInt(full, 16);
	const r = (int >> 16) & 255;
	const g = (int >> 8) & 255;
	const b = int & 255;

	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * N
 *
 * @param {number} angle
 * @returns {number}
 */
function normalizeAngle(angle) {
	return ((angle % 360) + 360) % 360;
}

/**
 * Calculate the coordinates of a point on a circle based on the given radius and angle in degrees.
 *
 * @param {number} radius
 * @param {number} angleDeg
 * @returns {{x, y}}
 */
function pointOnCircle(radius, angleDeg) {

	return {
		x: CENTER + Math.cos(degToRadius(angleDeg)) * radius,
		y: CENTER + Math.sin(degToRadius(angleDeg)) * radius,
	};
}

/**
 *
 * @param {{r: number, g:number, b:number}}
 * @returns {string}
 */
function rgbObjectToString({r, g, b}) {
	return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}


function lerp(from, to, t) {
	return from + (to - from) * t;
}

function lerpAngle(from, to, t) {
	return from + getShortestAngleDelta(from, to) * t;
}

function lerpColor(fromColor, toColor, t) {
	const from = colorToRgbObject(fromColor);
	const to = colorToRgbObject(toColor);

	return rgbObjectToString({
		r: lerp(from.r, to.r, t),
		g: lerp(from.g, to.g, t),
		b: lerp(from.b, to.b, t),
	});
}


function easeInOutCubic(t) {
	return t < 0.5
		? 4 * t * t * t
		: 1 - Math.pow(-2 * t + 2, 3) / 2;
}


export default function RadialWheel({
										segments = [],
										allowAutoplay = true,
										allowRotation = true,
										initialActiveSegment = null,
										initialActiveItem = null
									}) {
	// -------------------------------------------------------------------------------------------------------------
	// Setup
	// -------------------------------------------------------------------------------------------------------------

	// Map the segments to set all properties for rendering.
	const geometry = useMemo(() => {
		if (!segments.length) {
			return [];
		}

		const totalItems = segments.reduce((sum, segment) => sum + segment.items.length, 0);
		let currentAngle = 0;

		return segments.map((segment, segmentIndex) => {
			// calculate segment weights based on the amount on items
			const segmentWeight = segment.items.length / totalItems;
			const segmentAngle = segmentWeight * 360;

			// set the angles for the segment segment
			const rawStart = currentAngle;
			const rawEnd = currentAngle + segmentAngle;

			// raise the current angle for the next segments
			currentAngle += segmentAngle;

			// calculate start, end and center positions of the segment
			const start = rawStart;
			const end = rawEnd;
			const center = start + (end - start) / 2;
			const flip = !allowRotation && currentAngle > 115 && currentAngle < 245;

			// calculate item angles
			const itemAngles = distributeAngles(start, end, segment.items.length);

			// create item objects
			const items = segment.items.map((item, itemIndex) => {
				const angle = itemAngles[itemIndex];
				const point = pointOnCircle(ITEMS_RADIUS, itemAngles[itemIndex]);

				return {
					...item,
					segmentId: segment.id,
					segmentLabel: segment.label,
					color: segment.color,
					x: point.x,
					y: point.y,
					angle
				};
			});

			// calculate paths and positions for label
			const labelPath = describeLabelPath(LABEL_RADIUS, start, end, flip);
			const labelInnerPath = describeLabelPath(LABEL_INNER_RADIUS, start, end, flip);
			const labelOuterPath = describeLabelPath(LABEL_OUTER_RADIUS, start, end, flip);

			// calculate path for segment and segment ring
			const segmentPath = describeSegmentPath(start, end);
			const segmentRingPath = describeSegmentRingPath(start, end)

			return {
				...segment,
				start,
				end,
				center,
				labelPath,
				labelInnerPath,
				labelOuterPath,
				segmentPath,
				segmentRingPath,
				items,
			};
		});
	}, [segments]);

	// -------------------------------------------------------------------------------------------------------------
	// States
	// -------------------------------------------------------------------------------------------------------------
	const [autoplay, setAutoplay] = useState(allowAutoplay);

	const [activeItemId, setActiveItemId] = useState(
		initialActiveItem ?? segments[0]?.items?.[0]?.id ?? null
	);
	const [activeSegmentId, setActiveSegmentId] = useState(
		initialActiveSegment ?? segments[0]?.id ?? null
	);
	const [hoveredSegmentId, setHoveredSegmentId] = useState(null);

	const [indicator, setIndicator] = useState(() => {
		const initialSegment = geometry.find((segment) => segment.id === activeSegmentId) ?? geometry[0] ?? null;

		if (!initialSegment) {
			return null;
		}

		return {
			start: initialSegment.start,
			end: initialSegment.end,
			color: initialSegment.color,
		};
	});

	const [isMobile, setIsMobile] = useState(() => {
		if (typeof window === 'undefined') {
			return false;
		}

		return (window.innerWidth < 480) || (window.innerWidth >= 768 && window.innerWidth < 1024);
	});

	// -------------------------------------------------------------------------------------------------------------
	// References
	// -------------------------------------------------------------------------------------------------------------
	const intervalRef = useRef(null);

	const activeSegment = geometry.find((segment) => segment.id === activeSegmentId) ?? geometry[0] ?? null;
	const highlightedSegmentId = hoveredSegmentId ?? activeSegment?.id ?? null;

	const allItems = geometry.flatMap((segment) => segment.items);

	const activeItem =
		allItems.find((item) => item.id === activeItemId) ??
		activeSegment?.items?.[0] ??
		null;

	/**
	 * Activate an item and its segment.
	 * @param item
	 * @return {void}
	 */
	function activateItem(item) {
		setAutoplay(false);
		setActiveSegmentId(item.segmentId);
		setActiveItemId(item.id);
	}

	/**
	 * Activate a segment and its first item.
	 * @param segment
	 * @return {void}
	 */
	function activateSegment(segment) {
		setAutoplay(false);
		setActiveSegmentId(segment?.id ?? null);
		setActiveItemId(segment.items[0]?.id ?? null);
	}

	/**
	 * Highlight a segment and its first item if it is hovered.
	 * @param segment
	 * @return {void}
	 */
	function hoverSegment(segment) {
		setAutoplay(false);
		setHoveredSegmentId(segment?.id ?? null);
	}

	// Return early if no segments in geometry
	if (!geometry.length) {
		return null;
	}

	// -------------------------------------------------------------------------------------------------------------
	// Angle
	// -------------------------------------------------------------------------------------------------------------

	const targetAngle = isMobile ? 270 : 0;

	const desiredRotation = useMemo(() => {
		if (!activeItem?.angle && activeItem?.angle !== 0) {
			return 0;
		}

		return targetAngle - activeItem.angle;
	}, [activeItem, targetAngle]);

	const [wheelRotation, setWheelRotation] = useState(desiredRotation);

	useEffect(() => {
		if (!allowRotation) {
			setWheelRotation(0);
			return;
		}

		setWheelRotation((currentRotation) => {
			return getShortestRotation(currentRotation, desiredRotation);
		});
	}, [desiredRotation]);

	// -------------------------------------------------------------------------------------------------------------
	// Effects
	// -------------------------------------------------------------------------------------------------------------

	// Indicator
	useEffect(() => {
		if (!activeSegment) {
			return;
		}

		setIndicator((current) => {
			if (!current) {
				return {
					start: activeSegment.start,
					end: activeSegment.end,
					color: activeSegment.color,
				};
			}

			return current;
		});

		let rafId;
		const duration = 450;

		const from = indicator ?? {
			start: activeSegment.start,
			end: activeSegment.end,
			color: activeSegment.color,
		};

		const to = {
			start: activeSegment.start,
			end: activeSegment.end,
			color: activeSegment.color,
		};

		const fromSpan = from.end - from.start;
		const toSpan = to.end - to.start;

		const fromCenter = from.start + fromSpan / 2;
		const toCenter = to.start + toSpan / 2;

		const startTime = performance.now();

		function frame(now) {
			const rawT = Math.min((now - startTime) / duration, 1);
			const t = easeInOutCubic(rawT);

			const center = lerpAngle(fromCenter, toCenter, t);
			const span = lerp(fromSpan, toSpan, t);

			setIndicator({
				start: center - span / 2,
				end: center + span / 2,
				color: lerpColor(from.color, to.color, t),
			});

			if (rawT < 1) {
				rafId = requestAnimationFrame(frame);
			}
		}

		rafId = requestAnimationFrame(frame);

		return () => cancelAnimationFrame(rafId);
	}, [activeSegment]);

	const indicatorPath = indicator
		? describeSegmentRingPath(indicator.start, indicator.end)
		: '';

	const indicatorGradientStart = indicator
		? pointOnCircle(RING_RADIUS, indicator.start)
		: null;

	const indicatorGradientEnd = indicator
		? pointOnCircle(RING_RADIUS, indicator.end)
		: null;

	// Autoplay
	useEffect(() => {
		if (!autoplay) {

			return;
		}

		let segmentIndex = geometry.findIndex(
			(s) => s.id === activeSegmentId
		);

		if (segmentIndex === -1) {
			segmentIndex = 0;
		}

		let itemIndex = geometry[segmentIndex]?.items.findIndex(
			(i) => i.id === activeItemId
		);

		if (itemIndex === -1 || itemIndex == null) {
			itemIndex = 0;
		}

		function tick() {
			const segment = geometry[segmentIndex];

			if (!segment || !autoplay)
				return;

			const items = segment.items;

			itemIndex++;

			if (itemIndex >= items.length) {
				itemIndex = 0;
				segmentIndex = (segmentIndex + 1) % geometry.length;
			}

			const nextSegment = geometry[segmentIndex];
			const nextItem = nextSegment.items[itemIndex];

			if (nextSegment && nextItem) {
				setActiveSegmentId(nextSegment.id);
				setActiveItemId(nextItem.id);
			}
		}

		intervalRef.current = setInterval(tick, INTERVAL_TIMEOUT);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [geometry, autoplay]);

	// Resize
	useEffect(() => {
		function handleResize() {
			setIsMobile((window.innerWidth < 480) || (window.innerWidth >= 768 && window.innerWidth < 1024));
		}

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	let label = activeItem.label;
	let description = activeItem.description;

	label = label.replace('&amp;', '&');
	description = description.replace('&amp;', '&');

	// Render Element
	return (
		<>
			<div className="service-wheel__mobile-info" aria-live="polite" aria-atomic="true">
				{activeItem && (
					<>
						{activeItem.link?.trim() && (
							<a
								className="service-wheel__mobile-info-anchor"
								href={activeItem.link}
								title={label}
							></a>
						)}
						<i
							className={`service-wheel__mobile-info-icon service-wheel__info-icon service-wheel__info-icon--${activeItem.icon.class}`}
							style={{backgroundColor: activeItem.color}}
						></i>
						<div className="service-wheel__mobile-info-content">
							<h3 className="service-wheel__mobile-info-title">{label}</h3>
							<p className="service-wheel__mobile-info-description">{description}</p>
						</div>
					</>
				)}
			</div>

			<div
				className="service-wheel__stage"
				style={{
					'--service-wheel-rotation': `${wheelRotation}deg`,
				}}
			>

			{/* Info */}
			<div className="service-wheel__info" aria-live="polite">
				{activeItem && (
					<>
						{activeItem.link?.trim() && (
							<a className="service-wheel__info-anchor" href={activeItem.link} title={label}></a>
						)}
						<i className={`service-wheel__info-icon service-wheel__info-icon--${activeItem.icon.class}`}
						   style={{backgroundColor: activeItem.color}}></i>
						<h3 className="service-wheel__info-title">{label}</h3>
						<p className="service-wheel__info-description">{description}</p>
					</>
				)}
			</div>

			<svg
				className="service-wheel__svg"
				viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
				role="img"
			>

				{/* Definitions */}
				<defs>
					{indicator && indicatorGradientStart && indicatorGradientEnd && (
						<linearGradient
							id="service-wheel-indicator-gradient"
							gradientUnits="userSpaceOnUse"
							x1={indicatorGradientStart.x}
							y1={indicatorGradientStart.y}
							x2={indicatorGradientEnd.x}
							y2={indicatorGradientEnd.y}
						>
							<stop offset="0%" stopColor={indicator.color}/>
							<stop offset="100%" stopColor="#ffffff"/>
						</linearGradient>
					)}

					{/* Path definitions for Segment Labels */}
					{geometry.map((segment) => {
						const separateLabel = segment.label.length > 9;

						return separateLabel
							? (
								<>
									<path
										id={`${segment.id}-label-inner-path`}
										key={`${segment.id}-label-inner-path`}
										d={segment.labelInnerPath}
										fill="none"
									/>
									<path
										id={`${segment.id}-label-outer-path`}
										key={`${segment.id}-label-outer-path`}
										d={segment.labelOuterPath}
										fill="none"
									/>
								</>
							)
							: (
								<path
									id={`${segment.id}-label-path`}
									key={`${segment.id}-label-path`}
									d={segment.labelPath}
									fill="none"
								/>
							)
					})}
				</defs>

				{/* Rotor */}
				<g className="service-wheel__rotor">

					{/* Ring */}
					<circle
						className={"service-wheel__ring"}
						cx={CENTER}
						cy={CENTER}
						r={RING_RADIUS}
						fill="none"
						stroke="var(--white)"
						strokeDasharray="12 8"
					/>

					{geometry.map((segment) => {
						const isActive = segment.id === activeSegmentId;
						const isHighlighted = segment.id === highlightedSegmentId;

						const separateLabel = segment.label.length > 9;
						let labelPart1 = segment.label;
						let labelPart2 = '';

						if (separateLabel) {
							const words = segment.label.split(' ');
							const middle = Math.ceil(words.length / 2);

							labelPart1 = words.slice(0, middle).join(' ');
							labelPart2 = words.slice(middle).join(' ');
						}

						return (
							<g key={`${segment.id}-segment`}>

								{/* Segment */}
								<path
									d={segment.segmentPath}
									className={[
										'service-wheel__segment',
										isActive ? 'is-active' : '',
										isHighlighted ? 'is-highlighted' : '',
									].filter(Boolean).join(' ')}
									fill={
										isActive || isHighlighted
											? segment.color
											: hexToRgba(segment.color, 0.20)
									}
									role="button"
									tabIndex={0}
									onMouseEnter={() => hoverSegment(segment)}
									onMouseLeave={() => hoverSegment(null)}
									onClick={() => activateSegment(segment)}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											activateSegment(segment);
										}
									}}
									aria-label={segment.label}
								/>

								{separateLabel && (
									<text
										className="service-wheel__segment-label"
										textAnchor={'middle'}
										dominantBaseline={"middle"}
									>
										<textPath
											href={`#${segment.id}-label-outer-path`}
											startOffset="50%"
											textAnchor="middle"
										>
											{labelPart1}
										</textPath>
										<textPath
											href={`#${segment.id}-label-inner-path`}
											startOffset="50%"
											textAnchor="middle"
										>
											{labelPart2}
										</textPath>
									</text>
								)}
								{!separateLabel && (
									<text
										className="service-wheel__segment-label"
										textAnchor={'middle'}
										dominantBaseline={"middle"}
									>
										<textPath
											href={`#${segment.id}-label-path`}
											startOffset="50%"
											textAnchor="middle"
										>
											{segment.label}
										</textPath>
									</text>
								)}
							</g>
						);
					})}

					{/* Indicator */}
					{indicator && (
						<g className="service-wheel__indicator-layer">
							<path
								className="service-wheel__indicator-path"
								d={indicatorPath}
								fill="url(#service-wheel-indicator-gradient)"
							/>
						</g>
					)}

					{geometry.flatMap((segment) =>
						segment.items.map((item) => {
							const isHighlighted =
								segment.id === activeSegmentId || segment.id === highlightedSegmentId;
							const isActive = item.id === activeItemId;

							return (
								<g
									key={`${item.id}-bubble`}
									className={[
										'service-wheel__item',
										isActive ? 'is-active' : '',
										isHighlighted ? 'is-highlighted' : '',
									].filter(Boolean).join(' ')}
									transform={`translate(${item.x} ${item.y})`}
									role="button"
									tabIndex={0}
									aria-label={`${segment.label}: ${item.label}`}
									aria-pressed={isActive}
									onMouseEnter={() => hoverSegment(segment)}
									onMouseLeave={() => hoverSegment(null)}
									onClick={() => activateItem(item)}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											activateItem(item);
										}
									}}
								>
									<g
										className={"service-wheel__item-inner"}
										style={{
											'--service-wheel-item-rotation': `${-wheelRotation}deg`,
										}}
									>
										<circle
											className="service-wheel__item-bg"
											r="21"
											fill={isActive || isHighlighted ? item.color : hexToRgba(item.color, 0.2)}
										/>
										<circle
											className="service-wheel__item-border"
											r="21"
											fill="none"
											stroke={isActive ? 'var(--white)' : 'transparent'}
											strokeWidth="2"
										/>
										<image
											className="service-wheel__item-icon-image"
											href={item.icon.url}
											x="-9"
											y="-9"
											width="18"
											height="18"
											preserveAspectRatio="xMidYMid meet"
										/>
									</g>
								</g>
							);
						})
					)}

				</g>

			</svg>

			</div>
		</>
	);
}
